/**
 * Die Bausteinfläche einer Dokumentvorschau: auswählen, am Griff verschieben, im Text schreiben.
 *
 * **Die eine** Mechanik für beide Dokumenteditoren — Erscheinungsbild und Systemtexte. Was sie
 * unterscheidet, ist ausschließlich, *was* mit einer Änderung geschieht: Das Erscheinungsbild
 * schreibt sie in seine Bausteine, ein Textsatz als Abweichung von seiner Vorlage. Die Bedienung
 * selbst ist dieselbe und darf es deshalb nur einmal geben.
 *
 * Die Fläche kennt weder React noch die Datenmodelle der beiden Seiten: Sie bekommt ein Dokument
 * und Rückrufe und meldet, was der Nutzer getan hat.
 */

import { rememberInlineSelection } from "./inlineRichText";

const TAG_PARAGRAPH = "P";
const CSS_POSITION_STATIC = "static";
const KEY_DELETE = "Delete";
const SECOND_SHEET = 2;
const HALF = 0.5;
/** Klick knapp neben einen Baustein: so nah (vertikal) muss er liegen, um getroffen zu werden. */
const NEAREST_BLOCK_TOLERANCE_PX = 14;
const NEAREST_BLOCK_SIDE_TOLERANCE_PX = 20;
const DRAG_GHOST_MAX_WIDTH_PX = 360;
const DRAG_GHOST_OFFSET_PX = 14;
/** Ab diesem Abstand zum nächsten Baustein gilt kein Einfügeziel. */
const DROP_TARGET_MAX_DISTANCE_PX = 60;
const DROP_INDICATOR_HALF_THICKNESS_PX = 2;
/** Das Logo rastet links, mittig oder rechts ein: Drittel des Blatts. */
const LOGO_LEFT_ZONE_END = 1 / 3;
const LOGO_RIGHT_ZONE_START = 2 / 3;

export type DocumentBlockCanvasBlock = {
  /** Kennung im Dokument (`data-mq-block`). */
  id: string;
  /** Sprechender Schlüssel; der Anker, über den ein abgeleitetes Dokument den Baustein anspricht. */
  key?: string | null;
  /** Ob der Baustein im Dokument verschoben werden darf. Feste Blattanker sind es nicht. */
  movable: boolean;
  /** Ob sein Text unmittelbar im Dokument geschrieben wird. */
  writable: boolean;
  /**
   * Das Logo wird nicht in der Folge verschoben, sondern quer über das Blatt gezogen — links,
   * mittig, rechts. Es behält deshalb seine Fläche als Griff; ein Bild hat keinen Text, der beim
   * Ziehen versehentlich markiert würde.
   */
  logo?: boolean;
  /** Ob der Baustein für dieses Dokument ausgeblendet ist. */
  hidden?: boolean;
  /** Beim Ziehen darf nur davor bzw. nur dahinter abgelegt werden. */
  dropBefore?: boolean;
  dropAfter?: boolean;
  /**
   * Ob der Baustein zum Blattfuß gehört — der Fußbereich selbst und alles, was dort bereits
   * verankert ist. Wer hier ablegt, will nicht ans Ende des Textflusses, sondern an den Blattfuß;
   * ohne diese Angabe wären beide Absichten dieselbe Lücke.
   */
  bottomArea?: boolean;
};

/** Wo ein Baustein abgelegt wurde: im Textfluss oder am Blattfuß. */
export type DocumentBlockDropZone = "flow" | "bottom";

export type DocumentBlockCanvasOptions = {
  blocks: DocumentBlockCanvasBlock[];
  /** Hinweis in einem leeren, beschreibbaren Baustein — sonst wäre er unsichtbar und nicht klickbar. */
  emptyHint?: string;
  /** Auswahl geändert; `blockLevel` sagt, ob am Griff (Baustein) oder im Text angefasst wurde; `page` nennt Seite 1 oder 2. */
  onSelect: (blockId: string | null, blockLevel: boolean, page?: 1 | 2) => void;
  /** Ein Baustein wurde vor oder hinter einen anderen gezogen; `zone` nennt den Bereich. */
  onReorder: (blockId: string, overId: string, placement: "before" | "after", zone: DocumentBlockDropZone) => void;
  /** Das Logo wurde quer über das Blatt gezogen. */
  onLogoPosition?: (blockId: string, position: "left" | "center" | "right") => void;
  /** Im Text wurde geschrieben; `footerGroup` nennt die Gruppe des Fußtexts, sonst `null`. */
  onTextChange: (blockId: string, element: HTMLElement, footerGroup: number | null) => void;
  /** Entfernen über die Entfernen-Taste. */
  onRemove?: (blockId: string, page?: 1 | 2) => void;
  /** Rückruf beim Fallenlassen eines Bausteins aus der externen Palette in das Blatt. */
  onDropAddBlock?: (type: string, target?: { sheet: 1 | 2; targetBlockId?: string | null; placement?: "before" | "after"; zone?: DocumentBlockDropZone }) => void;
  /** Prüft, ob der Baustein auf dieser Seite abgelegt werden darf. */
  isBlockAllowed?: (type: string, sheet: 1 | 2) => boolean;
  /**
   * Sichtbarkeit umschalten. Der Knopf sitzt am Baustein selbst und nicht in der Werkzeugleiste:
   * So liegt er in beiden Editoren am gleichen Ort — auch dort, wo es gar keine Bausteinleiste
   * gibt, weil nur Inhalte gepflegt werden.
   */
  onToggleVisible?: (blockId: string, visible: boolean, page?: 1 | 2) => void;
  /**
   * Das Element, in dem gerade geschrieben wird — oder `null`. Darüber setzt die Werkzeugleiste
   * ihre Variablen an der Schreibmarke ein, statt sie ans Ende zu hängen.
   */
  onWritableFocus?: (element: HTMLElement | null) => void;
  /** Die aktuelle Auswahl, damit die Fläche sie nach dem Neuaufbau kennt. */
  selectedBlockId: string | null;
  /**
   * Woran gearbeitet wird: am Aufbau oder am Text. Der Modus ersetzt den früheren Doppelklick — der
   * war fummelig, weil jeder Klick raten musste, was gemeint ist, und weil man zum Verschieben erst
   * wieder aus dem Text herausklicken musste.
   */
  mode: "design" | "text";
};

/**
 * Der Rahmen liegt **innen** (`outline-offset:-1px`) und nicht außen.
 *
 * Bausteine stoßen im Dokument aneinander; ein nach außen gesetzter Rahmen legte sich über den
 * Nachbarn, und an jeder Grenze standen zwei Linien übereinander. Aus demselben Grund liegt der
 * Hover-Schein innen: Ein äußerer Schein deckte den darüberliegenden Baustein zu.
 */
const DESIGN_STYLE = `
  [data-mq-block]{border-radius:4px;outline:1px dashed rgba(114,46,209,.3);outline-offset:-1px;}
  [data-mq-block]:hover:not(.mq-block-selected){outline-style:solid;box-shadow:inset 0 0 0 2px rgba(114,46,209,.08);}
  [data-mq-block].mq-rule, [data-mq-block]:empty{outline-offset:0;}
  [data-mq-block].mq-rule:hover, [data-mq-block]:empty:hover{outline:1px solid rgba(114,46,209,.7);box-shadow:0 0 0 2px rgba(114,46,209,.1);}`;

const STYLE = `
  [data-mq-block]{position:relative;}
  [data-mq-block]:not(.mq-block-writable){cursor:pointer;user-select:none;-webkit-user-select:none;}
  [data-mq-block]:not(.mq-block-writable) *{user-select:none;-webkit-user-select:none;}
  [data-mq-block] p:last-child{margin-bottom:0 !important;}
  /* Eine Trennlinie bleibt in beiden Modi eine Null-Höhen-Box. Im Designmodus enthält sie den
     absolut positionierten Griff und ist deshalb nicht :empty; im Textmodus fehlt der Griff.
     Würde die allgemeine Leerblock-Höhe dort greifen, spränge dieselbe Linie beim Umschalten
     um 12 px. */
  [data-mq-block].mq-rule{min-height:0;cursor:pointer;}
  [data-mq-block]:empty:not(.mq-rule){min-height:12px;cursor:pointer;}
  [data-mq-block].mq-rule::before{content:"";position:absolute;top:-6px;bottom:-6px;left:0;right:0;cursor:pointer;z-index:1;}
  [data-mq-block]:empty:not(.mq-rule)::before{content:"";position:absolute;top:-8px;bottom:-8px;left:0;right:0;cursor:pointer;z-index:1;}
  .mq-block-writable{min-height:1.2em;}
  .mq-block-writable[contenteditable=true]{cursor:text;user-select:text;-webkit-user-select:text;}
  .mq-block-writable[contenteditable=true] *{user-select:text;-webkit-user-select:text;}
  .mq-template-value{display:inline-block;padding:0 2px;border-radius:2px;background:rgba(114,46,209,.10);color:inherit;user-select:all;-webkit-user-select:all;}
  /* design-rule-exception: raw-color Dokument-Linkfarbe wie Word/Outlook und SFDT-Vorschau */
  a,.mq-template-value[href]{color:#0563c1;text-decoration:underline;pointer-events:none;}
  .mq-block-writable:empty::before{content:attr(data-mq-empty-hint);opacity:.45;}
  .mq-block-writable[contenteditable=true]:focus{outline:2px solid rgba(114,46,209,.5);outline-offset:3px;}
  [data-mq-block].mq-block-selected{outline:2px solid rgba(114,46,209,.5) !important;outline-offset:3px !important;border-radius:4px;}
  [data-mq-block].mq-rule.mq-block-selected{outline-offset:0 !important;}
  .mq-logo-movable{cursor:grab;touch-action:none;}
  .mq-block-hidden{opacity:.4;}
  .mq-block-handle{position:absolute;left:-22px;top:0;width:16px;height:18px;display:flex;align-items:center;justify-content:center;
    border-radius:4px;color:#722ed1;background:rgba(114,46,209,.08);font-size:12px;line-height:1;cursor:grab;touch-action:none;
    opacity:0;transition:opacity .12s;user-select:none;-webkit-user-select:none;z-index:2;}
  [data-mq-block].mq-rule > .mq-block-handle{top:50%;transform:translateY(-50%);}
  [data-mq-block]:empty > .mq-block-handle{top:-3px;}
  [data-mq-block]:hover > .mq-block-handle,.mq-block-handle.mq-block-handle-active{opacity:1;}
  .mq-block-dragging{cursor:grabbing;opacity:.35;filter:saturate(.7);}
  .mq-block-drag-ghost{position:fixed;z-index:2147483647;pointer-events:none;box-sizing:border-box;max-height:120px;overflow:hidden;opacity:.92;background:#fff;border:1px solid #722ed1;border-radius:6px;box-shadow:0 10px 28px rgba(0,0,0,.24);transform:scale(.98);}
  /* Die Lücke wird ausschließlich durch die Marke angezeigt. Ein zusätzlich hervorgehobener
     Zielbaustein sagte dasselbe ein zweites Mal — und wechselte dabei zwischen den beiden
     Bausteinen, die an derselben Lücke liegen. */
  .mq-block-drop-indicator{position:fixed;z-index:2147483646;height:4px;background:#722ed1;border-radius:999px;box-shadow:0 0 0 3px rgba(114,46,209,.14);pointer-events:none;}
  .mq-block-drop-indicator::before{content:"";position:absolute;left:-3px;top:-3px;width:10px;height:10px;border-radius:50%;background:#722ed1;}
`;

type Drag = {
  id: string;
  source: HTMLElement;
  logo: boolean;
  overId: string | null;
  placement: "before" | "after";
  zone: DocumentBlockDropZone;
  ghost: HTMLElement | null;
  pointerId: number;
  startX: number;
  startY: number;
  started: boolean;
};

/** Ab dieser Entfernung ist es ein Ziehen und kein Klick mehr. */
const DRAG_THRESHOLD_PX = 5;

/**
 * Was im Blatt steht, aber nicht zum Text gehört: die Bedienelemente dieser Fläche und das
 * Beiwerk des Renderers (`SystemDocumentHtmlWriter.AffixClass`) — etwa das „Betreff:" vor dem
 * Betreff. Beides wird vor dem Auslesen entfernt; sonst stünde es beim nächsten Speichern im Text.
 */
const NON_TEXT_SELECTOR = ".mq-block-handle, .mq-block-eye, .mq-block-affix";

// Gemeinsamer Vertrag mit
// backend/Services/Settings/Documents/SystemDocumentHtmlWriter.cs — beide Namen zusammen ändern.
const PARAGRAPH_SPACING_ATTRIBUTE = "data-mq-paragraph-spacing";

/** Der geschriebene Inhalt eines Elements, ohne Bedienelemente und Beiwerk. */
export function documentBlockTextSource(element: HTMLElement): HTMLElement {
  const clone = element.cloneNode(true) as HTMLElement;
  clone.querySelectorAll(NON_TEXT_SELECTOR).forEach((node) => node.remove());
  return clone;
}

/**
 * Zieht den sichtbaren Absatzabstand nach einer Eingabe sofort nach.
 *
 * Der letzte Absatz trägt immer Abstand null. Teilt Chromium ihn mit Enter, kopiert es diesen
 * Wert auf beide Absätze; erst der spätere Serverrender würde den ersten wieder richtig setzen.
 * Der Renderer schreibt deshalb den wirksamen Wert zusätzlich als Datenattribut an jeden Absatz.
 */
export function applyEditableParagraphLayout(element: HTMLElement): void {
  const parents = new Set<HTMLElement>();
  element.querySelectorAll<HTMLElement>("p").forEach((paragraph) => {
    if (paragraph.parentElement != null) parents.add(paragraph.parentElement);
  });

  parents.forEach((parent) => {
    const paragraphs = Array.from(parent.children)
      .filter((child) => child.tagName === TAG_PARAGRAPH) as HTMLElement[];
    const spacing = paragraphs
      .map((paragraph) => paragraph.getAttribute(PARAGRAPH_SPACING_ATTRIBUTE))
      .find((value): value is string => value != null && value.length > 0);
    if (spacing == null) return;

    paragraphs.forEach((paragraph, index) => {
      paragraph.setAttribute(PARAGRAPH_SPACING_ATTRIBUTE, spacing);
      paragraph.style.marginBottom = index === paragraphs.length - 1 ? "0" : spacing;
    });
  });
}

let activePaletteDragType: string | null = null;

export function setActivePaletteDragType(type: string | null): void {
  activePaletteDragType = type;
}

export function getActivePaletteDragType(): string | null {
  return activePaletteDragType;
}

/**
 * Richtet die Fläche im geladenen Dokument ein und liefert die Aufräumfunktion.
 */
export function activateDocumentBlockCanvas(doc: Document, options: DocumentBlockCanvasOptions): () => void {
  const { blocks, emptyHint, mode, onSelect, onReorder, onLogoPosition, onTextChange, onRemove, onDropAddBlock, isBlockAllowed, onWritableFocus } = options;
  const writing = mode === "text";

  // Ohne Vorgabe erzeugt Chromium abhängig vom Ausgangsmarkup <div>-Zeilen oder <p>-Absätze.
  // Der Renderer arbeitet kanonisch mit <p>; so greift dessen Absatzdarstellung schon beim Enter.
  if (writing) doc.execCommand("defaultParagraphSeparator", false, "p");

  const style = doc.createElement("style");
  // Im Textmodus verschwinden die Bausteinrahmen: Das Blatt sieht aus wie das fertige Schreiben,
  // und man erkennt ohne Hinsehen auf den Schalter, worin man gerade arbeitet.
  style.textContent = STYLE + (writing ? "" : DESIGN_STYLE);
  doc.head?.appendChild(style);

  const byId = new Map(blocks.map((block) => [block.id, block]));

  /**
   * Das Element zu einem Ereignisziel. Beim Schreiben und beim Markieren ist das Ziel häufig ein
   * **Textknoten** — der hat kein `closest`, und ein direkter Aufruf darauf beendet die Bearbeitung
   * mit einem Fehler. `instanceof Element` hilft hier nicht: Im eingebetteten Dokument ist es eine
   * andere Klasse als im Hauptfenster.
   */
  const asElement = (target: EventTarget | null): HTMLElement | null => {
    const node = target as Node | null;
    if (node == null) return null;
    if (typeof (node as HTMLElement).closest === "function") return node as HTMLElement;
    return node.parentElement;
  };

  const blockElement = (target: EventTarget | null) =>
    asElement(target)?.closest<HTMLElement>("[data-mq-block]") ?? null;

  let drag: Drag | null = null;
  let suppressClick = false;
  let selectedBlockId = options.selectedBlockId;
  let selectedPage: 1 | 2 = 1;

  /** Merkt die Auswahl **und** zeigt sie: beides gehört zusammen, sonst driftet eines ab. */
  const selectBlock = (id: string | null, page?: 1 | 2) => {
    selectedBlockId = id;
    if (page != null) selectedPage = page;
    doc.querySelectorAll(".mq-block-selected").forEach((node) => node.classList.remove("mq-block-selected"));
    if (id == null) return;
    const selector = page === SECOND_SHEET
      ? `.mq-sheet-2 [data-mq-block="${CSS.escape(id)}"]`
      : page === 1
        ? `.mq-sheet-1 [data-mq-block="${CSS.escape(id)}"]`
        : `[data-mq-block="${CSS.escape(id)}"]`;
    (doc.querySelector(selector) ?? doc.querySelector(`[data-mq-block="${CSS.escape(id)}"]`))?.classList.add("mq-block-selected");
  };

  if (selectedBlockId != null) {
    selectBlock(selectedBlockId);
  }

  const dropIndicator = doc.createElement("div");
  dropIndicator.className = "mq-block-drop-indicator";
  const clearDropTarget = () => dropIndicator.remove();

  /**
   * Beschreibbar heißt hier: **kann** beschrieben werden. Geschrieben wird erst nach einem
   * Doppelklick — ein einfacher Klick gilt dem Baustein.
   *
   * Diese Trennung ist der Grund, warum die Fläche überhaupt eindeutig bedienbar ist: Wäre der Text
   * durchgehend beschreibbar, wäre jedes Anfassen eines Bausteins zugleich ein Textcursor, und
   * jedes Markieren eines Wortes ein Verschieben.
   */
  const makeWritable = (element: HTMLElement) => {
    element.classList.add("mq-block-writable");
    element.contentEditable = writing ? "true" : "false";
    element.spellcheck = true;
    if (emptyHint) element.setAttribute("data-mq-empty-hint", emptyHint);
  };

  doc.querySelectorAll<HTMLElement>("[data-mq-block]").forEach((element) => {
    const id = element.getAttribute("data-mq-block");
    const block = id == null ? undefined : byId.get(id);
    if (block == null) return;

    if (block.logo) element.classList.add("mq-logo-movable");

    if (block.writable) {
      // Der Fußtext steht in Zellen — jede trägt ihre Gruppe und wird einzeln beschrieben.
      const cells = element.querySelectorAll<HTMLElement>("[data-mq-footer-group]");
      if (cells.length > 0) cells.forEach(makeWritable);
      else makeWritable(element);
    }

    // Im Textmodus gibt es nichts zu verschieben — und ein nicht beschreibbares Kind im Element
    // stört beim Setzen der Schreibmarke.
    const hasHandle = !writing && block.movable && !block.logo;
    if (hasHandle) {
      if (doc.defaultView?.getComputedStyle(element).position === CSS_POSITION_STATIC) {
        element.style.position = "relative";
      }
      const handle = doc.createElement("div");
      handle.className = "mq-block-handle";
      handle.setAttribute("contenteditable", "false");
      handle.textContent = "⠿";
      element.insertBefore(handle, element.firstChild);
    }

    if (block.hidden === true) {
      element.classList.add("mq-block-hidden");
    }

    element.draggable = false;
    element.querySelectorAll<HTMLElement>("img, a").forEach((child) => { child.draggable = false; });
  });

  const onClick = (event: MouseEvent) => {
    if (asElement(event.target)?.closest("a") != null) event.preventDefault();
    if (suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      suppressClick = false;
      return;
    }

    const targetEl = asElement(event.target);
    const sheet: 1 | 2 = targetEl?.closest(".mq-sheet-2") != null ? SECOND_SHEET : 1;

    let element = blockElement(event.target);
    if (element == null && !writing) {
      // Falls knapp neben eine Trennlinie oder einen flachen Baustein geklickt wurde:
      const clientY = event.clientY;
      const clientX = event.clientX;
      const candidates = Array.from(doc.querySelectorAll<HTMLElement>("[data-mq-block]"));
      let closestBlock: HTMLElement | null = null;
      let minDistance = NEAREST_BLOCK_TOLERANCE_PX;
      for (const cand of candidates) {
        const rect = cand.getBoundingClientRect();
        if (clientX >= rect.left - NEAREST_BLOCK_SIDE_TOLERANCE_PX && clientX <= rect.right + NEAREST_BLOCK_SIDE_TOLERANCE_PX) {
          const distY = Math.max(0, rect.top - clientY, clientY - rect.bottom);
          if (distY < minDistance) {
            minDistance = distY;
            closestBlock = cand;
          }
        }
      }
      if (closestBlock != null) {
        element = closestBlock;
      }
    }

    const id = element?.getAttribute("data-mq-block") ?? null;
    selectBlock(id, sheet);

    if (writing) {
      // Im Textmodus setzt jeder Klick die Schreibmarke; die Leiste zeigt die Zeichen. Der Fokus
      // wird ausdrücklich gesetzt: Ein Klick auf den Rand eines Absatzes trifft sonst den Block
      // statt seines Textes, und die Schreibmarke bliebe aus.
      const target = targetEl?.closest<HTMLElement>(".mq-block-writable")
        ?? element?.querySelector<HTMLElement>(".mq-block-writable")
        ?? null;
      if (target != null && doc.activeElement !== target) target.focus();
      onWritableFocus?.(target);
      onSelect(id, false, sheet);
      return;
    }

    onWritableFocus?.(null);
    onSelect(id, id != null, sheet);
  };

  const onPointerDown = (event: PointerEvent) => {
    // Im Textmodus wird geschrieben und markiert, nicht verschoben.
    if (writing || event.button !== 0 || !event.isPrimary) return;
    let source = blockElement(event.target);
    if (source == null) {
      const clientY = event.clientY;
      const clientX = event.clientX;
      const candidates = Array.from(doc.querySelectorAll<HTMLElement>("[data-mq-block]"));
      let minDistance = NEAREST_BLOCK_TOLERANCE_PX;
      for (const cand of candidates) {
        const rect = cand.getBoundingClientRect();
        if (clientX >= rect.left - NEAREST_BLOCK_SIDE_TOLERANCE_PX && clientX <= rect.right + NEAREST_BLOCK_SIDE_TOLERANCE_PX) {
          const distY = Math.max(0, rect.top - clientY, clientY - rect.bottom);
          if (distY < minDistance) {
            minDistance = distY;
            source = cand;
          }
        }
      }
    }
    const id = source?.getAttribute("data-mq-block");
    const block = id == null ? undefined : byId.get(id);
    if (!source || !id || block == null || (!block.movable && !block.logo)) return;

    const onHandle = asElement(event.target)?.closest(".mq-block-handle") != null;
    if (!onHandle && !block.logo) return;

    const sheet: 1 | 2 = source?.closest(".mq-sheet-2") != null ? SECOND_SHEET : 1;
    selectBlock(id, sheet);
    onSelect(id, true, sheet);
    drag = {
      id,
      source,
      logo: block.logo === true,
      overId: null,
      placement: "before",
      zone: "flow",
      ghost: null,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      started: false,
    };
    source.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event: PointerEvent) => {
    if (drag == null || drag.pointerId !== event.pointerId) return;
    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY);
    if (!drag.started && distance < DRAG_THRESHOLD_PX) return;
    event.preventDefault();

    if (!drag.started) {
      drag.started = true;
      drag.source.classList.add("mq-block-dragging");
      const rect = drag.source.getBoundingClientRect();
      const ghost = drag.source.cloneNode(true) as HTMLElement;
      ghost.removeAttribute("data-mq-block");
      ghost.className = "mq-block-drag-ghost";
      ghost.style.width = `${Math.min(rect.width, DRAG_GHOST_MAX_WIDTH_PX)}px`;
      doc.body.appendChild(ghost);
      drag.ghost = ghost;
    }

    if (drag.ghost) {
      drag.ghost.style.left = `${event.clientX + DRAG_GHOST_OFFSET_PX}px`;
      drag.ghost.style.top = `${event.clientY + DRAG_GHOST_OFFSET_PX}px`;
    }

    if (drag.logo) {
      const frame = doc.body.getBoundingClientRect();
      if (frame.width > 0) {
        const x = (event.clientX - frame.left) / frame.width;
        drag.source.style.textAlign = x < LOGO_LEFT_ZONE_END ? "left" : x > LOGO_RIGHT_ZONE_START ? "right" : "center";
      }
      return;
    }

    let target = blockElement(doc.elementFromPoint(event.clientX, event.clientY));
    const allBlocks = Array.from(doc.querySelectorAll<HTMLElement>("[data-mq-block]"))
      .filter((element) => element.getAttribute("data-mq-block") !== drag?.id);

    // Wenn der Zeiger zwischen Blöcken (im Rand/Abstand) steht, das nächstgelegene Element ermitteln
    if (!target && allBlocks.length > 0) {
      let minDistance = Infinity;
      let closest: HTMLElement | null = null;
      for (const el of allBlocks) {
        const r = el.getBoundingClientRect();
        const distY = event.clientY < r.top ? r.top - event.clientY : (event.clientY > r.bottom ? event.clientY - r.bottom : 0);
        if (distY < minDistance) {
          minDistance = distY;
          closest = el;
        }
      }
      if (closest && minDistance < DROP_TARGET_MAX_DISTANCE_PX) {
        target = closest;
      }
    }

    const overId = target?.getAttribute("data-mq-block");
    const overBlock = overId == null ? undefined : byId.get(overId);
    if (!target || !overId || overBlock == null || overId === drag.id) {
      drag.overId = null;
      clearDropTarget();
      return;
    }

    // Der Bereich entscheidet sich am Element, bei dem der Zeiger steht — vor der Umrechnung auf
    // die Lücke. Danach zeigt der Anker oft auf den oberen Nachbarn, und der steht im Textfluss,
    // auch wenn abgelegt wurde, wo der Blattfuß beginnt.
    drag.zone = overBlock.bottomArea === true ? "bottom" : "flow";

    const rect = target.getBoundingClientRect();
    let placement: "before" | "after" = event.clientY < rect.top + rect.height * HALF ? "before" : "after";
    if (overBlock.dropBefore) placement = "before";
    if (overBlock.dropAfter) placement = "after";

    /*
      Hinter dem einen und vor dem nächsten Baustein ist **dieselbe** Lücke. Deshalb wird die Stelle
      zuerst als Lücke gerechnet und danach immer über denselben Anker beschrieben — bevorzugt
      „hinter dem oberen Baustein". Ohne diesen Schritt trug die Fläche für eine einzige mögliche
      Position zwei verschiedene Ziele, je nachdem, in welcher Hälfte der Zeiger stand.
    */
    const others = allBlocks;
    const gap = others.indexOf(target) + (placement === "after" ? 1 : 0);
    const above = others[gap - 1] ?? null;
    const below = others[gap] ?? null;
    const blockOf = (element: HTMLElement | null) =>
      element == null ? undefined : byId.get(element.getAttribute("data-mq-block") ?? "");
    // Ein fester Blattanker gibt vor, auf welcher Seite abgelegt werden darf; der Anker wechselt
    // deshalb auf den unteren Baustein, wenn der obere „nur davor" verlangt.
    const anchor = blockOf(above)?.dropBefore !== true && above != null
      ? { element: above, placement: "after" as const }
      : blockOf(below)?.dropAfter !== true && below != null
        ? { element: below, placement: "before" as const }
        : null;
    if (anchor == null) {
      drag.overId = null;
      clearDropTarget();
      return;
    }

    drag.overId = anchor.element.getAttribute("data-mq-block");
    drag.placement = anchor.placement;
    clearDropTarget();

    // Die Marke liegt in der Mitte der Lücke und spannt über beide angrenzenden Bausteine.
    const aboveRect = above?.getBoundingClientRect() ?? null;
    const belowRect = below?.getBoundingClientRect() ?? null;
    const gapY = aboveRect != null && belowRect != null
      ? (aboveRect.bottom + belowRect.top) * HALF
      : aboveRect?.bottom ?? belowRect?.top ?? rect.top;
    const left = Math.min(aboveRect?.left ?? rect.left, belowRect?.left ?? rect.left);
    const right = Math.max(aboveRect?.right ?? rect.right, belowRect?.right ?? rect.right);

    dropIndicator.style.left = `${left}px`;
    dropIndicator.style.top = `${gapY - DROP_INDICATOR_HALF_THICKNESS_PX}px`;
    dropIndicator.style.width = `${right - left}px`;
    doc.body.appendChild(dropIndicator);
  };

  // Nur die Waagerechte wird noch gebraucht: Das Logo folgt ihr über das Blatt, die Bausteinfolge
  // legt beim Loslassen die Stelle ab, die die Marke bereits anzeigte.
  const finishDrag = (clientX: number) => {
    const completed = drag;
    drag = null;
    clearDropTarget();
    if (completed == null) return;

    completed.source.releasePointerCapture?.(completed.pointerId);
    if (!completed.started) return;
    completed.source.classList.remove("mq-block-dragging");
    completed.ghost?.remove();
    suppressClick = true;
    doc.defaultView?.setTimeout(() => { suppressClick = false; }, 0);

    if (completed.logo) {
      const frame = doc.body.getBoundingClientRect();
      if (frame.width <= 0) return;
      const x = (clientX - frame.left) / frame.width;
      onLogoPosition?.(completed.id, x < LOGO_LEFT_ZONE_END ? "left" : x > LOGO_RIGHT_ZONE_START ? "right" : "center");
      return;
    }

    // Abgelegt wird genau dort, wo die Marke stand. Das Ziel beim Loslassen erneut unter dem Zeiger
    // zu suchen, führte zu einer anderen Stelle als der angezeigten, sobald der Zeiger dabei um ein
    // paar Pixel wanderte oder in der anderen Hälfte desselben Bausteins stand.
    const overId = completed.overId;
    if (!overId || overId === completed.id) return;

    // Das Dokument folgt sofort; der Serverabgleich bestätigt dieselbe Reihenfolge kurz darauf.
    // Ohne diesen Schritt sähe das Ziehen bis dahin wirkungslos aus.
    const target = Array.from(doc.querySelectorAll<HTMLElement>("[data-mq-block]"))
      .find((element) => element.getAttribute("data-mq-block") === overId) ?? null;
    if (target?.parentElement === completed.source.parentElement) {
      if (completed.placement === "after") target.after(completed.source);
      else target.before(completed.source);
    }

    onReorder(completed.id, overId, completed.placement, completed.zone);
  };

  const onPointerUp = (event: PointerEvent) => {
    if (drag == null || drag.pointerId !== event.pointerId) return;
    finishDrag(event.clientX);
  };

  const onPointerCancel = (event: PointerEvent) => {
    if (drag == null || drag.pointerId !== event.pointerId) return;
    const cancelled = drag;
    drag = null;
    clearDropTarget();
    cancelled.source.classList.remove("mq-block-dragging");
    cancelled.ghost?.remove();
    cancelled.source.releasePointerCapture?.(cancelled.pointerId);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== KEY_DELETE || selectedBlockId == null || onRemove == null) return;
    // Im Text ist die Entfernen-Taste ein Zeichen weniger, nicht ein Baustein weniger.
    if (asElement(event.target)?.closest("[contenteditable=\"true\"]")) return;
    event.preventDefault();
    onRemove(selectedBlockId, selectedPage);
    selectBlock(null);
  };

  /**
   * Wo geschrieben wird, muss sich Text markieren lassen; überall sonst stört eine Auswahl nur beim
   * Ziehen. Geprüft wird `contenteditable` und nicht die eigene Klasse: Im Systemtext-Editor sind
   * die Textfelder des Vorgangs auf demselben Blatt beschreibbar, ohne dass diese Fläche sie kennt.
   */
  /**
   * Hält die Schreibmarke fest, solange im Dokument gearbeitet wird. Sie geht verloren, sobald die
   * Werkzeugleiste den Fokus bekommt — und genau dann wird sie zum Einfügen gebraucht.
   */
  const onSelectionChange = () => {
    const selection = doc.getSelection();
    const anchor = selection?.anchorNode;
    const element = asElement(anchor ?? null)?.closest<HTMLElement>(".mq-block-writable");
    if (element != null) rememberInlineSelection(element);
  };

  const onSelectStart = (event: Event) => {
    const element = blockElement(event.target);
    if (element && asElement(event.target)?.closest("[contenteditable=\"true\"]") == null) {
      event.preventDefault();
    }
  };

  const onInput = (event: Event) => {
    const element = asElement(event.target)?.closest<HTMLElement>(".mq-block-writable");
    const id = element?.closest<HTMLElement>("[data-mq-block]")?.getAttribute("data-mq-block");
    if (!element || !id) return;
    applyEditableParagraphLayout(element);
    const group = element.getAttribute("data-mq-footer-group");
    onTextChange(id, element, group == null ? null : Number(group));
  };

  /** Eingefügter Text kommt als reiner Text an; fremde Formatierung hat im Dokument nichts verloren. */
  const onPaste = (event: ClipboardEvent) => {
    if (!asElement(event.target)?.closest(".mq-block-writable")) return;
    event.preventDefault();
    doc.execCommand("insertText", false, event.clipboardData?.getData("text/plain") ?? "");
    onInput(event);
  };

  /**
   * Drag & Drop von Bausteinen aus der externen Palette in das Blatt.
   */
  const onDragOver = (event: DragEvent) => {
    if (writing) return;
    const isBlockAdd = event.dataTransfer?.types.includes("application/x-mq-block-type")
      || event.dataTransfer?.types.includes("text/plain");
    if (!isBlockAdd) return;

    event.preventDefault();

    const targetEl = asElement(event.target);
    const sheet: 1 | 2 = targetEl?.closest(".mq-sheet-2") != null ? SECOND_SHEET : 1;
    const dragType = activePaletteDragType;

    // Wenn der Baustein auf dieser Seite nicht erlaubt ist (z. B. Betreff auf Seite 2 oder Logo bereits auf dieser Seite):
    if (dragType && isBlockAllowed && !isBlockAllowed(dragType, sheet)) {
      if (event.dataTransfer) event.dataTransfer.dropEffect = "none";
      clearDropTarget();
      return;
    }

    if (event.dataTransfer) event.dataTransfer.dropEffect = "copy";

    let target = blockElement(doc.elementFromPoint(event.clientX, event.clientY));
    const allDocBlocks = Array.from(doc.querySelectorAll<HTMLElement>("[data-mq-block]"));

    if (!target && allDocBlocks.length > 0) {
      let minDistance = Infinity;
      let closest: HTMLElement | null = null;
      for (const el of allDocBlocks) {
        const r = el.getBoundingClientRect();
        const distY = event.clientY < r.top ? r.top - event.clientY : (event.clientY > r.bottom ? event.clientY - r.bottom : 0);
        if (distY < minDistance) {
          minDistance = distY;
          closest = el;
        }
      }
      if (closest && minDistance < DROP_TARGET_MAX_DISTANCE_PX) {
        target = closest;
      }
    }

    const overId = target?.getAttribute("data-mq-block");
    const overBlock = overId == null ? undefined : byId.get(overId);
    if (!target || !overId || overBlock == null) {
      clearDropTarget();
      return;
    }

    const rect = target.getBoundingClientRect();
    let placement: "before" | "after" = event.clientY < rect.top + rect.height * HALF ? "before" : "after";
    if (overBlock.dropBefore) placement = "before";
    if (overBlock.dropAfter) placement = "after";

    const others = allDocBlocks;
    const gap = others.indexOf(target) + (placement === "after" ? 1 : 0);
    const above = others[gap - 1] ?? null;
    const below = others[gap] ?? null;
    const blockOf = (element: HTMLElement | null) =>
      element == null ? undefined : byId.get(element.getAttribute("data-mq-block") ?? "");
    const anchor = blockOf(above)?.dropBefore !== true && above != null
      ? { element: above, placement: "after" as const }
      : blockOf(below)?.dropAfter !== true && below != null
        ? { element: below, placement: "before" as const }
        : null;

    if (anchor == null) {
      clearDropTarget();
      return;
    }

    clearDropTarget();
    const aboveRect = above?.getBoundingClientRect() ?? null;
    const belowRect = below?.getBoundingClientRect() ?? null;
    const gapY = aboveRect != null && belowRect != null
      ? (aboveRect.bottom + belowRect.top) * HALF
      : aboveRect?.bottom ?? belowRect?.top ?? rect.top;
    const left = Math.min(aboveRect?.left ?? rect.left, belowRect?.left ?? rect.left);
    const right = Math.max(aboveRect?.right ?? rect.right, belowRect?.right ?? rect.right);

    dropIndicator.style.left = `${left}px`;
    dropIndicator.style.top = `${gapY - DROP_INDICATOR_HALF_THICKNESS_PX}px`;
    dropIndicator.style.width = `${right - left}px`;
    doc.body.appendChild(dropIndicator);
  };

  const onDragLeave = (event: DragEvent) => {
    if (event.relatedTarget == null || (event.clientX <= 0 || event.clientY <= 0)) {
      clearDropTarget();
    }
  };

  const onDrop = (event: DragEvent) => {
    if (writing) return;
    const blockType = event.dataTransfer?.getData("application/x-mq-block-type")
      || event.dataTransfer?.getData("text/plain")
      || activePaletteDragType;
    if (!blockType) return;

    event.preventDefault();
    clearDropTarget();

    const targetEl = asElement(event.target);
    const sheet: 1 | 2 = targetEl?.closest(".mq-sheet-2") != null ? SECOND_SHEET : 1;

    if (isBlockAllowed && !isBlockAllowed(blockType, sheet)) {
      return;
    }

    let target = blockElement(doc.elementFromPoint(event.clientX, event.clientY));
    const allDocBlocks = Array.from(doc.querySelectorAll<HTMLElement>("[data-mq-block]"));
    if (!target && allDocBlocks.length > 0) {
      let minDistance = Infinity;
      let closest: HTMLElement | null = null;
      for (const el of allDocBlocks) {
        const r = el.getBoundingClientRect();
        const distY = event.clientY < r.top ? r.top - event.clientY : (event.clientY > r.bottom ? event.clientY - r.bottom : 0);
        if (distY < minDistance) {
          minDistance = distY;
          closest = el;
        }
      }
      if (closest && minDistance < DROP_TARGET_MAX_DISTANCE_PX) {
        target = closest;
      }
    }

    const overId = target?.getAttribute("data-mq-block") ?? null;
    const rect = target?.getBoundingClientRect();
    const placement: "before" | "after" = rect && event.clientY < rect.top + rect.height * HALF ? "before" : "after";
    const zone: DocumentBlockDropZone =
      (overId != null && byId.get(overId)?.bottomArea === true) ? "bottom" : "flow";

    onDropAddBlock?.(blockType, { sheet, targetBlockId: overId, placement, zone });
  };

  doc.addEventListener("selectionchange", onSelectionChange);
  doc.addEventListener("click", onClick);
  doc.addEventListener("pointerdown", onPointerDown);
  doc.addEventListener("pointermove", onPointerMove, { passive: false });
  doc.addEventListener("pointerup", onPointerUp);
  doc.addEventListener("pointercancel", onPointerCancel);
  doc.addEventListener("selectstart", onSelectStart);
  doc.addEventListener("keydown", onKeyDown);
  doc.addEventListener("input", onInput);
  doc.addEventListener("paste", onPaste);
  doc.addEventListener("dragover", onDragOver);
  doc.addEventListener("dragleave", onDragLeave);
  doc.addEventListener("drop", onDrop);

  return () => {
    doc.removeEventListener("selectionchange", onSelectionChange);
    doc.removeEventListener("click", onClick);
    doc.removeEventListener("pointerdown", onPointerDown);
    doc.removeEventListener("pointermove", onPointerMove);
    doc.removeEventListener("pointerup", onPointerUp);
    doc.removeEventListener("pointercancel", onPointerCancel);
    doc.removeEventListener("selectstart", onSelectStart);
    doc.removeEventListener("keydown", onKeyDown);
    doc.removeEventListener("input", onInput);
    doc.removeEventListener("paste", onPaste);
    doc.removeEventListener("dragover", onDragOver);
    doc.removeEventListener("dragleave", onDragLeave);
    doc.removeEventListener("drop", onDrop);
    // Was diese Fläche ins Dokument geschrieben hat, nimmt sie auch wieder heraus. Sonst sammelt
    // sich bei jedem Neuaufsetzen — etwa beim Umschalten zwischen Gestaltung und Text — ein
    // weiterer Griff je Baustein an, und die Bearbeitbarkeit bliebe auf dem alten Stand.
    doc.querySelectorAll(".mq-block-handle, .mq-block-eye").forEach((node) => node.remove());
    doc.querySelectorAll(".mq-block-selected").forEach((node) => node.classList.remove("mq-block-selected"));
    doc.querySelectorAll<HTMLElement>(".mq-block-writable").forEach((element) => {
      element.classList.remove("mq-block-writable");
      element.contentEditable = "inherit";
      element.removeAttribute("data-mq-empty-hint");
    });
    doc.querySelectorAll<HTMLElement>(".mq-logo-movable").forEach((element) => {
      element.classList.remove("mq-logo-movable");
    });
    drag?.source.classList.remove("mq-block-dragging");
    if (drag != null) drag.source.releasePointerCapture?.(drag.pointerId);
    drag?.ghost?.remove();
    clearDropTarget();
    style.remove();
  };
}
